import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import { ValidateDesignData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { CC_ANONYMOUS_USER_STORAGE_KEY } from '@app/checkout/modules/checkout-shared/services/store-persist.service';
import { Direction } from '@app/core/models/step';
import {
	ProductFlowPage,
	getStepData,
	setStepStateValid,
} from '@app/product/reducers/product-flow.reducer';
import { SelectedOptionsEvent } from '@app/product/reducers/product.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { hasSelectableOptions, isVariableProduct } from '@app/product/utils/product-utils';
import { getIsUploadDone, getIsUploadSuccessful } from '@app/shared/reducers/upload.reducer';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { ViewportService } from '@app/shared/services/viewport.service';
import { getUser } from '@app/user/reducers/user.reducer';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'g-validate-design',
	templateUrl: './validate-design.component.html',
	styleUrls: ['./validate-design.component.scss'],
	standalone: false,
})
export class ValidateDesignComponent implements OnInit {
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly productEditor = inject(ProductEditorService);
	private readonly newRelic = inject(NewRelic);
	readonly viewport = inject(ViewportService);

	@Input() readonly product?: api.ProductDto;
	@Input() readonly showPrices = true;
	@Output() readonly selectedOptionsChanged = new EventEmitter<SelectedOptionsEvent>();
	productImages: string[] = [];
	initialOptions?: api.OrderProductSelectedOptionsDto;
	readonly price$ = this.productEditor.price$;

	// TODO: Refactor to use signals
	get hasSelectableOptions() {
		return this.product ? hasSelectableOptions(this.product) : false;
	}
	get isVariableProduct() {
		return this.product ? isVariableProduct(this.product) : false;
	}

	readonly isUploadDone$ = this.store.select(
		getIsUploadDone(this.productEditor.getPrintfile()?.key || ''),
	);
	readonly isUploadSuccessful$ = combineLatest([
		this.isUploadDone$,
		this.store.select(getIsUploadSuccessful(this.productEditor.getPrintfile()?.key || '')),
	]).pipe(map(([isDone, isSuccessful]) => isDone && isSuccessful));
	readonly isUploadFailure$ = combineLatest([
		this.isUploadDone$,
		this.store.select(getIsUploadSuccessful(this.productEditor.getPrintfile()?.key || '')),
	]).pipe(map(([isDone, isSuccessful]) => isDone && !isSuccessful));

	readonly isValidated$ = new BehaviorSubject<boolean>(false);
	icons = { faSpinner, faCircleCheck, faTriangleExclamation };
	private user?: api.UserDto;

	ngOnInit(): void {
		this.productEditor.selectedOptions$
			.pipe(untilDestroyed(this), first())
			.subscribe((selectedOptions) => {
				this.initialOptions = selectedOptions || this.productEditor.initialOptions;
			});

		this.store
			.select(getUser)
			.pipe(untilDestroyed(this))
			.subscribe((user) => {
				this.user = user;
			});

		this.isValidated$.pipe(untilDestroyed(this)).subscribe((isDesignValidated) => {
			this.store.dispatch(setStepStateValid(!!this.productImages && isDesignValidated));
		});

		combineLatest([
			this.store.select(getStepData(this.productEditor.itemId, ProductFlowPage.ProductDesigner)),
			this.store.select(getStepData(this.productEditor.itemId, ProductFlowPage.ValidateDesign)),
		])
			.pipe(untilDestroyed(this))
			.subscribe(([designerData, validateData]) => {
				// Get product editor images from data saved in product designer step
				const cartProductImages = this.productEditor.productFlowData?.scaledProofImages || [];
				const stepProductImages = designerData?.scaledProofImages;
				this.productImages = stepProductImages || cartProductImages;

				// Get previous validated status if it exists
				const isDesignValidated = validateData?.isDesignValidated ?? false;
				this.isValidated$.next(isDesignValidated);
			});
	}

	collectStepData(direction?: Direction): ValidateDesignData | undefined {
		if (direction === Direction.Previous) return;
		const data = { isDesignValidated: this.isValidated$.getValue() };
		this.productEditor.patchProductFlowData(data);
		return data;
	}

	async retryUpload(): Promise<void> {
		const printfile = this.productEditor.getPrintfile();
		const stateId = printfile?.editor_state_id;
		const userId = this.user?.id || localStorage.getItem(CC_ANONYMOUS_USER_STORAGE_KEY);
		// istanbul ignore next -- If this happens, there's a bug somewhere or something was done to localStorage, so let's log it.
		if (!userId) {
			const error = new Error('(ValidateDesign.retryUpload) Missing user ID');
			this.newRelic.noticeError(error);
			throw error;
		}
		if (stateId) {
			await this.productEditor.uploadPrintfile(stateId, userId, false);
		} else {
			const key = this.productEditor.getPrintfile()?.key || 'N/A';
			const error = new Error(`(ValidateDesign.retryUpload) Missing state ID: ${key}`);
			this.newRelic.noticeError(error);
			throw error;
		}
	}
}
